import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePartActionDefaults } from './usePartActionDefaults';
import { PartLocationType } from '../../../../../@types/part.type';
import { Action, PartActionType } from '../../../../../@types/partAction.type';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
  PartOrPartTypeSearchData,
} from '../../../../../@types/partType.type';
import { useSearchPartsOrPartTypes } from '../../../../../queries/live-search-query';

export interface PartActionSearchFieldProps {
  label: string;
  clientId: number;
  contractId: number;
  locationId: number;
  serviceId: number;
  actionType: string;
  /** Whether this field is searching for the IN (installed) or OUT (removed) part. */
  action: Action;
  /**
   * Reported-serial part id from the parent service. Drives the *serial*-specific
   * defaults for DOA OUT (the user is picking the faulty serial being DOA'd)
   * and surfaces the reported serial at the top of IN-side dropdowns.
   */
  reportedSerialNumberPartId?: number;
  onSelect: (item: PartTypeActionLiveSearchItem | null) => void;
}

const SERIALISED_OUT_ACTIONS = new Set([
  PartActionType.REPLACE,
  PartActionType.REMOVE,
  PartActionType.DOA,
]);

const PartActionSearchOption: React.FC<{ option: PartTypeActionLiveSearchItem }> = ({ option }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
      {option.partTypeName || option.name}
      {option.serial1 && (
        <Typography component="span" sx={{ fontSize: '11px', color: 'text.secondary', ml: 0.5 }}>
          ({option.serial1})
        </Typography>
      )}
    </Typography>
    <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
      {option.categoryCode} &middot; {option.conditionCode || '—'} &middot;{' '}
      {option.locationName || '—'}
    </Typography>
  </Box>
);

export const PartActionSearchField: React.FC<PartActionSearchFieldProps> = ({
  label,
  clientId,
  contractId,
  locationId,
  serviceId,
  actionType,
  action,
  reportedSerialNumberPartId,
  onSelect,
}) => {
  const searchMutation = useSearchPartsOrPartTypes();
  const [inputValue, setInputValue] = useState('');
  const [searchOptions, setSearchOptions] = useState<PartTypeActionLiveSearchItem[]>([]);
  const [selected, setSelected] = useState<PartTypeActionLiveSearchItem | null>(null);
  // Set when inputValue is updated by a selection (not typing), so the live-
  // search effect skips the redundant request for the just-picked item.
  const skipSearchRef = useRef(false);

  const isOutSerialised =
    action === Action.OUT && SERIALISED_OUT_ACTIONS.has(actionType as PartActionType);
  const isInAction = actionType === PartActionType.INSTALL || action === Action.IN;

  const { defaults: defaultSuggestions } = usePartActionDefaults({
    clientId,
    contractId,
    locationId,
    serviceId,
    actionType,
    action,
    reportedSerialNumberPartId,
  });

  // Strip bare serialised part-types from IN-side live-search results — for
  // those actions the user must pick a specific serial or a consumable type.
  // Stable across renders with the same isInAction so the effect's dep list
  // doesn't churn.
  const filterLive = useCallback(
    (results: PartTypeActionLiveSearchItem[]): PartTypeActionLiveSearchItem[] =>
      isInAction
        ? results.filter(
            (r) =>
              !(
                r.searchItemType === 'part-type' &&
                r.categoryCode === PartTypeActionLiveSearchItemCategoryCode.Serialised
              )
          )
        : results,
    [isInAction]
  );

  // Live search — typed input always hits /partOrPartType.
  useEffect(() => {
    // A selection just set inputValue — don't fire a live search for it (the
    // picked item is already in the current options); typing resets the flag.
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }
    if (inputValue.length < 2) {
      setSearchOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      const data: PartOrPartTypeSearchData = {
        name: inputValue,
        serialiseType: isOutSerialised ? PartTypeActionLiveSearchItemCategoryCode.Serialised : '',
        clientContractIds: [clientId, contractId],
        locationId: locationId ? String(locationId) : '',
        allowedPartLocationType: isInAction ? PartLocationType.Node : '',
        partActionType: actionType,
        searchType: 'part-and-part-type',
      };
      searchMutation
        .mutateAsync(data)
        .then((results) => setSearchOptions(filterLive(results)))
        .catch(() => setSearchOptions([]));
    }, 300);
    return () => clearTimeout(timer);
    // searchMutation is a stable React Query mutation handle; adding it would
    // re-run this debounced effect on every render. Intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputValue,
    actionType,
    action,
    isOutSerialised,
    isInAction,
    clientId,
    contractId,
    locationId,
    filterLive,
  ]);

  // Below 2 chars we show the pre-loaded defaults; otherwise live results win.
  const options = useMemo<PartTypeActionLiveSearchItem[]>(
    () => (inputValue.length < 2 ? defaultSuggestions : searchOptions),
    [inputValue, defaultSuggestions, searchOptions]
  );

  return (
    <Box>
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
        {label}
      </Typography>
      <Autocomplete
        size="small"
        options={options}
        value={selected}
        // Two reference-stable selections compare as inequal under MUI's default
        // equality — match on key fields so the controlled value stays in sync.
        isOptionEqualToValue={(opt, val) =>
          opt.partTypeId === val.partTypeId &&
          (opt.partId ?? 0) === (val.partId ?? 0) &&
          (opt.serial1 ?? '') === (val.serial1 ?? '')
        }
        getOptionLabel={(opt) =>
          opt.serial1 ? `${opt.partTypeName} - ${opt.serial1}` : opt.partTypeName || opt.name
        }
        filterOptions={(x) => x}
        inputValue={inputValue}
        // Only sync inputValue from real keystrokes — when MUI fires `reset`
        // on selection/blur, ignore it so the dropdown reopens to the full
        // default list instead of filtering by the just-picked option's label.
        onInputChange={(_, val, reason) => {
          if (reason === 'input') setInputValue(val);
        }}
        onChange={(_, item) => {
          setSelected(item);
          onSelect(item);
          // Keep the controlled input in sync with the selection so the chosen
          // serial (or part-type name) stays visible and can be amended, rather
          // than showing the stale search term or nothing at all. Flag it so the
          // live-search effect doesn't fire a redundant request for the pick.
          skipSearchRef.current = !!item;
          setInputValue(item ? item.serial1 || item.partTypeName || item.name || '' : '');
        }}
        // Only clear the filter on open when nothing is selected yet — that
        // preserves the "fresh field shows the full default list" behaviour.
        // Once a part is selected, keep its value so clicking back in lets the
        // user amend the serial instead of wiping it (previously every click
        // blanked the field).
        onOpen={() => {
          if (!selected) setInputValue('');
        }}
        loading={searchMutation.isPending}
        noOptionsText={
          inputValue.length < 2 && defaultSuggestions.length === 0
            ? 'Type at least 2 characters to search'
            : 'No matches'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search part type or serial..."
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={`${option.partTypeId}-${option.partId ?? option.ns_partId ?? option.name}`}
          >
            <PartActionSearchOption option={option} />
          </li>
        )}
      />
    </Box>
  );
};
